import { Button } from '@insify/design-system/components';
import { IconicFileDownload2 } from '@insify/design-system/iconic-icons';
import { ChevronLeft, ChevronRight } from '@insify/design-system/icons';
import { LogoWithText, LogoWithTextInverted } from '@insify/design-system/logo';
import { TrackedButton } from '@objectiv/tracker-react';
import clsx from 'clsx';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useChatStore } from '../../../../stores/chatStore';
import { useChatActivityTracking } from '../../utils/tracking';
import { Markdown } from '../Markdown/Markdown';

import styles from './Presentation.module.css';

import type { PresentationSlide } from '../../../../api/getChat';
import type { SwiperRef } from 'swiper/react';

import 'swiper/css';

interface MarkdownPresentationProps {
  slides: PresentationSlide[];
  variables: Record<string, unknown>;
  onSlideChange?: (index: number) => void;
}

const A4_WIDTH_MM = 297;
const A4_HEIGHT_MM = 210;
const DPI = 96;
const MM_TO_PX = (mm: number) => (mm * DPI) / 25.4;

const DOWNLOAD_ACTIVE = false;

async function convertImagesToBase64(container: HTMLElement) {
  const images = container.querySelectorAll('img');
  for (const img of images) {
    const url = img.src;
    try {
      const res = await fetch(url, { mode: 'cors' });
      const blob = await res.blob();
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      img.src = base64;
    } catch (err) {
      console.warn(`Failed to inline image ${url}`, err);
    }
  }
}

const MarkdownPresentation: React.FC<MarkdownPresentationProps> = ({ slides }) => {
  const { t } = useTranslation('assistant');

  const [narratedSlides, setNarratedSlides] = useState<boolean[]>(Array(slides.length).fill(false));
  const addAiResponse = useChatStore.use.addAIResponse();
  const activeSlideIndex = useChatStore.use.activeSlideIndex();
  const setActiveSlideIndex = useChatStore.use.setActiveSlideIndex();

  const printRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<SwiperRef>(null);

  const setRelatedQuestions = useChatStore.use.setRelatedQuestions();

  const { trackSlideVisibleEvent } = useChatActivityTracking();

  const onSlideChange = (slideIndex: number) => trackSlideVisibleEvent(`slide-${slideIndex + 1}`);

  const downloadAsPdf = async () => {
    const element = printRef.current;
    if (!element) return;

    const pdf = new jsPDF('l', 'mm', 'a4');
    const slideEls = Array.from(element.children) as HTMLElement[];

    for (let i = 0; i < slideEls.length; i++) {
      const slideEl = slideEls[i];

      // Ensure images are embedded before rendering
      await convertImagesToBase64(slideEl);
      const canvas = await html2canvas(slideEl, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true,
        imageTimeout: 15000,
        logging: true,
        onclone: (clonedDoc) => {
          const images = clonedDoc.querySelectorAll('img');
          console.log('Cloned images:', images);
          images.forEach((img) => {
            img.setAttribute('crossorigin', 'anonymous');
          });
        },
      });

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Failed to get canvas context');
        return;
      }
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      const imgProps = pdf.getImageProperties(imgData);

      const link = document.createElement('a');
      link.download = 'test-slide.png';
      link.href = canvas.toDataURL();
      link.click();

      const pdfWidth = A4_WIDTH_MM;
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
    }

    pdf.save('presentation.pdf');
  };

  const narrateSlide = (slideIndex: number) => {
    if (narratedSlides[slideIndex] || !slides[slideIndex].narration) return;
    // Play audio for the current slide
    addAiResponse({
      response: slides[slideIndex].narration,
    });

    setNarratedSlides((prev) => {
      const newSlides = [...prev];
      newSlides[slideIndex] = true;
      return newSlides;
    });
  };

  const goToNextSlide = () => {
    if (sliderRef.current && activeSlideIndex < slides.length - 1) {
      sliderRef.current.swiper.slideNext();
    }
  };

  const goToPreviousSlide = () => {
    if (sliderRef.current && activeSlideIndex > 0) {
      sliderRef.current.swiper.slidePrev();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* On-screen Swiper Presentation */}
      <Swiper
        modules={[Pagination, Navigation, Keyboard]}
        navigation={false}
        keyboard={{ enabled: true }}
        className={styles.swiper}
        ref={sliderRef}
        onInit={(swiper) => {
          setTimeout(() => {
            narrateSlide(swiper.activeIndex);
          }, 2000);
          if (onSlideChange) {
            onSlideChange(swiper.activeIndex);
          }
        }}
        onSlideChange={(swiper) => {
          setActiveSlideIndex(swiper.activeIndex);
          narrateSlide(swiper.activeIndex);
          setRelatedQuestions(slides[swiper.activeIndex].questions);
          if (onSlideChange) {
            onSlideChange(swiper.activeIndex);
          }
        }}
      >
        {slides.map((slide, idx) => (
          <SwiperSlide
            // className={clsx(styles.slide, slideThemeClass[slide.theme as keyof typeof slideThemeClass])}
            className={clsx(styles.slide, slideThemeClass[slide.theme as keyof typeof slideThemeClass])}
            // eslint-disable-next-line react/no-array-index-key
            key={`slide-${idx}`}
          >
            <div className={clsx(styles.slideInner, styles[`${slide.theme}Inner`])}>
              <div className={styles.slideContent}>
                <Markdown>{slide.content}</Markdown>
              </div>
              <div className={styles.slideFooter}>
                <div className={styles.footerLeftImage}>
                  {slide.image && (
                    <img
                      src={slide.image}
                      alt='Slide visual'
                      style={{ maxWidth: '100%', maxHeight: '100%' }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                </div>
                {slide.theme === 'dark' ? <LogoWithTextInverted height={20} /> : <LogoWithText height={20} />}
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Custom Navigation Buttons */}
      <div className={styles.navigation}>
        <Button
          kind='secondary'
          type='button'
          onClick={goToPreviousSlide}
          disabled={activeSlideIndex === 0}
          trackingId='presentation-previous'
          leftIcon={<ChevronLeft />}
        />

        {/* Pagination dots */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {slides.map((slide, idx) => (
            <TrackedButton
              objectiv={{
                id: `slide-dot-${idx + 1}`,
              }}
              // eslint-disable-next-line react/no-array-index-key
              key={`dot-${slide.content.slice(0, 20)}-${idx}`}
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: idx === activeSlideIndex ? '#000000' : '#d1d5db',
                transition: 'background-color 0.2s ease',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (sliderRef.current) {
                  sliderRef.current.swiper.slideTo(idx);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (sliderRef.current) {
                    sliderRef.current.swiper.slideTo(idx);
                  }
                }
              }}
              tabIndex={0}
              role='button'
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>

        <Button
          kind={activeSlideIndex < slides.length - 1 ? 'primary' : 'neutral'}
          type='button'
          onClick={goToNextSlide}
          disabled={activeSlideIndex === slides.length - 1}
          trackingId='presentation-next'
          rightIcon={<ChevronRight />}
        >
          {t('presentation_next')}
        </Button>
      </div>

      {DOWNLOAD_ACTIVE && (
        <>
          <Button
            leftIcon={<IconicFileDownload2 />}
            kind='primary'
            type='button'
            onClick={downloadAsPdf}
            trackingId='download-presentation'
            style={{ padding: 5, position: 'fixed', top: 10, right: 50, zIndex: 1000 }}
          ></Button>
          {/* Off-screen printable slides */}
          <div
            ref={printRef}
            style={{
              position: 'absolute',
              top: '-9999px',
              left: '-9999px',
              opacity: 0,
              width: `${MM_TO_PX(A4_WIDTH_MM)}px`,
              height: `${MM_TO_PX(A4_HEIGHT_MM)}px`,
            }}
          >
            {slides.map((slide, idx) => (
              <div
                key={`print-slide-${idx}`}
                className={clsx(styles.slide, slideThemeClass[slide.theme as keyof typeof slideThemeClass])}
              >
                <Slide slide={slide} isPrint />
                {/* <div
          //   key={`print-slide-${idx}`}
          //   style={{
          //     width: `${MM_TO_PX(A4_WIDTH_MM)}px`,
          //     minHeight: `${MM_TO_PX(A4_HEIGHT_MM)}px`,
          //     padding: '40px',
          //     fontSize: '16px',
          //     backgroundColor: '#fff',
          //     boxSizing: 'border-box',
          //   }}
          // >
          //   <Markdown>{content.content}</Markdown>
          // </div> */}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

interface SlideProps {
  isPrint?: boolean;
  className?: string;
  slide: PresentationSlide;
}

const Slide: React.FC<SlideProps> = ({ slide, className, isPrint }) => {
  const { content, theme, image } = slide;
  return (
    <div className={clsx(styles.slideInner, styles[`${theme}Inner`], className)}>
      <div className={styles.slideContent}>
        <Markdown isPrint={isPrint}>{content}</Markdown>
      </div>
      <div className={styles.slideFooter}>
        <div className={styles.footerLeftImage}>
          {image && (
            <img
              src={image}
              alt='Slide visual'
              style={{ maxWidth: '100%', maxHeight: '100%' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
        </div>
        {theme === 'dark' ? <LogoWithTextInverted height={20} /> : <LogoWithText height={20} />}
      </div>
    </div>
  );
};

const slideThemeClass = {
  white: styles.slideWhite,
  green: styles.slideGreen,
  dark: styles.slideDark,
  lightGreen: styles.slideLightGreen,
  putty: styles.slidePutty,
  purple: styles.slidePurple,
};

export { MarkdownPresentation };
